import { Injectable } from '@angular/core';
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';
import {
  CERTIFICATE_SHEET_H,
  CERTIFICATE_SHEET_W,
  CertificateImageElement,
  CertificateTemplate,
  CertificateTextElement,
  CertificateVars,
  isImageElement,
  isTextElement,
  resolveCertificateVars,
  svgIntrinsicSize,
} from '../model/certificate-template';

type Crop = { zoom: number, offsetX: number, offsetY: number };
const FULL_CROP: Crop = { zoom: 1, offsetX: 0, offsetY: 0 };

/**
 * Geração do PDF A4 paisagem do certificado (pdf-lib), compartilhada entre a
 * página do certificado do autor e o editor de template do chair.
 *
 * `generateFromTemplate`: template JSON — fundo + imagens (crop aplicado em
 * offscreen canvas, pois pdf-lib não recorta) + caixas de texto.
 *
 * Todo o texto é VETORIAL (família Times padrão do PDF — sem embutir fonte);
 * a geometria vem do canvas fixo 1123×794px convertido a pt (mesma quebra de
 * linha da tela).
 */
@Injectable({ providedIn: 'root' })
export class CertificatePdfService {

  // A4 paisagem em pt.
  private readonly pageW = 841.89;
  private readonly pageH = 595.28;
  private get ptPerPx(): number { return this.pageW / CERTIFICATE_SHEET_W; }

  /** Caminho por TEMPLATE: fundo → imagens → texto (mesma ordem de camadas da tela). */
  async generateFromTemplate(template: CertificateTemplate, vars: CertificateVars): Promise<Uint8Array> {
    const doc = await PDFDocument.create();
    const page = doc.addPage([this.pageW, this.pageH]);

    if (template.background && template.background.url) {
      await this.drawBackground(doc, page, template.background.url, template.background.opacity);
    }
    for (const el of template.elements.filter(isImageElement)) {
      if (el.url) { await this.drawImageElement(doc, page, el); }
    }

    // Fontes só das variantes usadas (família × bold × italic) — sob demanda.
    // Itálico de Helvetica/Courier nas fontes-padrão do PDF chama-se Oblique.
    const VARIANTS: { [family: string]: StandardFonts[] } = {
      times: [StandardFonts.TimesRoman, StandardFonts.TimesRomanBold,
              StandardFonts.TimesRomanItalic, StandardFonts.TimesRomanBoldItalic],
      helvetica: [StandardFonts.Helvetica, StandardFonts.HelveticaBold,
                  StandardFonts.HelveticaOblique, StandardFonts.HelveticaBoldOblique],
      courier: [StandardFonts.Courier, StandardFonts.CourierBold,
                StandardFonts.CourierOblique, StandardFonts.CourierBoldOblique],
    };
    const fontCache = new Map<string, PDFFont>();
    const getFont = async (family: string, bold: boolean, italic: boolean): Promise<PDFFont> => {
      const variants = VARIANTS[family] || VARIANTS['times'];
      const key = `${family}-${bold}-${italic}`;
      if (!fontCache.has(key)) {
        fontCache.set(key, await doc.embedFont(variants[(bold ? 1 : 0) + (italic ? 2 : 0)]));
      }
      return fontCache.get(key);
    };

    for (const el of template.elements.filter(isTextElement)) {
      await this.drawTextElement(page, el, vars, await getFont(el.font, el.bold, el.italic));
    }
    return doc.save();
  }

  // ---------------------------------------------------------------- imagens

  /** Fundo full-bleed "cover" da página inteira (vetorial quando SVG). */
  private async drawBackground(doc: PDFDocument, page: PDFPage, url: string, opacity: number): Promise<void> {
    await this.drawArtwork(doc, page, url,
      { x: 0, y: 0, w: CERTIFICATE_SHEET_W, h: CERTIFICATE_SHEET_H },
      FULL_CROP, Math.min(1, Math.max(0, opacity)));
  }

  /** Elemento de imagem: crop por moldura (vetorial quando SVG). */
  private async drawImageElement(doc: PDFDocument, page: PDFPage, el: CertificateImageElement): Promise<void> {
    await this.drawArtwork(doc, page, el.url, { x: el.x, y: el.y, w: el.w, h: el.h }, el.crop, 1);
  }

  /**
   * Desenha uma arte (fundo ou elemento) na moldura dada (coords do canvas).
   * SVG → caminho VETORIAL: converte p/ PDF de 1 página no browser (jsPDF +
   * svg2pdf, carregados sob demanda) e embute a página com bounding box — o
   * próprio bbox faz o crop por moldura, sem rasterizar. Qualquer falha do
   * conversor (SVG exótico) cai no caminho raster (offscreen canvas 2×).
   */
  private async drawArtwork(
    doc: PDFDocument, page: PDFPage, url: string,
    frame: { x: number, y: number, w: number, h: number }, crop: Crop, opacity: number,
  ): Promise<void> {
    const blob = await fetch(url).then(r => r.ok ? r.blob() : null).catch(() => null);
    if (!blob) { return; }
    const isSvg = blob.type.includes('svg') || /\.svg(\?|$)/i.test(url);

    const dest = {
      x: frame.x * this.ptPerPx,
      y: this.pageH - (frame.y + frame.h) * this.ptPerPx,
      width: frame.w * this.ptPerPx,
      height: frame.h * this.ptPerPx,
    };

    if (isSvg) {
      try {
        const embedded = await this.embedSvgVector(doc, await blob.text(), frame.w, frame.h, crop);
        page.drawPage(embedded, { ...dest, opacity });
        return;
      } catch { /* conversor não deu conta — raster abaixo */ }
    }

    const img = await this.loadImageFromBlob(blob, isSvg);
    if (!img) { return; }
    const png = await this.cropToCanvas(img, frame.w, frame.h, crop);
    if (!png) { return; }
    page.drawImage(await doc.embedPng(png), { ...dest, opacity });
  }

  /**
   * SVG → página PDF vetorial embutida, já recortada pela moldura.
   * O bounding box do embedPage é calculado invertendo o crop-por-moldura:
   * a região da ARTE visível pela moldura vira o bbox (clip vetorial), e o
   * drawPage escala o resultado para a moldura.
   */
  private async embedSvgVector(doc: PDFDocument, svgText: string, frameW: number, frameH: number, crop: Crop) {
    const dims = svgIntrinsicSize(svgText);
    if (!dims) { throw new Error('svg sem dimensões'); }

    // innerHTML em nó SOLTO não executa <script>; svg2pdf lê o elemento.
    const holder = document.createElement('div');
    holder.innerHTML = svgText;
    const svgEl = holder.querySelector('svg');
    if (!svgEl) { throw new Error('svg inválido'); }
    svgEl.setAttribute('width', String(dims.w));
    svgEl.setAttribute('height', String(dims.h));

    const { jsPDF } = await import('jspdf');
    await import('svg2pdf.js');   // side-effect: adiciona doc.svg()
    const converter = new jsPDF({
      unit: 'pt',
      format: [dims.w, dims.h],
      orientation: dims.w >= dims.h ? 'landscape' : 'portrait',
    });
    await (converter as any).svg(svgEl, { x: 0, y: 0, width: dims.w, height: dims.h });

    const source = await PDFDocument.load(converter.output('arraybuffer'));
    const [sourcePage] = source.getPages();

    // Inversão do crop: mesma matemática do cropToCanvas, resolvida em
    // unidades da ARTE (a página do conversor tem o tamanho da arte em pt).
    const cover = Math.max(frameW / dims.w, frameH / dims.h);
    const scale = cover * (crop.zoom || 1);
    const left = (frameW - dims.w * scale) / 2 + (crop.offsetX || 0);
    const top = (frameH - dims.h * scale) / 2 + (crop.offsetY || 0);
    const ax = -left / scale;
    const ayTop = -top / scale;
    const aw = frameW / scale;
    const ah = frameH / scale;

    return doc.embedPage(sourcePage, {
      left: ax,
      right: ax + aw,
      bottom: dims.h - (ayTop + ah),
      top: dims.h - ayTop,
    });
  }

  /**
   * Reproduz o crop-por-moldura da tela num canvas 2× (nitidez no PDF):
   * escala "cover" × zoom, centro deslocado por offsetX/Y (px do canvas).
   */
  private cropToCanvas(
    img: HTMLImageElement, frameW: number, frameH: number,
    crop: { zoom: number, offsetX: number, offsetY: number },
  ): Promise<Uint8Array | null> {
    const DPR = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(frameW * DPR));
    canvas.height = Math.max(1, Math.round(frameH * DPR));
    const ctx = canvas.getContext('2d');
    if (!ctx) { return Promise.resolve(null); }

    const cover = Math.max(frameW / img.naturalWidth, frameH / img.naturalHeight);
    const scale = cover * (crop.zoom || 1);
    const drawW = img.naturalWidth * scale * DPR;
    const drawH = img.naturalHeight * scale * DPR;
    const left = (frameW * DPR - drawW) / 2 + (crop.offsetX || 0) * DPR;
    const top = (frameH * DPR - drawH) / 2 + (crop.offsetY || 0) * DPR;
    ctx.drawImage(img, left, top, drawW, drawH);

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        if (!blob) { resolve(null); return; }
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.onerror = () => resolve(null);
        reader.readAsArrayBuffer(blob);
      }, 'image/png');
    });
  }

  /**
   * Carrega a imagem p/ o caminho raster; falha vira null (arte pulada).
   * SVG só-viewBox reporta naturalWidth 0 → injeta width/height (do viewBox)
   * no texto antes de criar o object URL, senão o canvas não desenha.
   */
  private async loadImageFromBlob(blob: Blob, isSvg: boolean): Promise<HTMLImageElement | null> {
    let source = blob;
    if (isSvg) {
      const text = await blob.text().catch(() => null);
      const dims = text && svgIntrinsicSize(text);
      if (!text || !dims) { return null; }
      // Re-serializa via DOM (setAttribute não duplica atributo — atributo
      // duplicado é erro FATAL no parse XML do <img src=svg>).
      const parsed = new DOMParser().parseFromString(text, 'image/svg+xml').querySelector('svg');
      if (!parsed) { return null; }
      parsed.setAttribute('width', String(dims.w));
      parsed.setAttribute('height', String(dims.h));
      source = new Blob([new XMLSerializer().serializeToString(parsed)], { type: 'image/svg+xml' });
    }
    const url = URL.createObjectURL(source);
    try {
      return await new Promise<HTMLImageElement | null>(resolve => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth ? img : null);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  // ------------------------------------------------------------------ texto

  /** Caixa de texto: quebra na largura da caixa, alinhamento e fluxo top-down. */
  private drawTextElement(page: PDFPage, el: CertificateTextElement, vars: CertificateVars, font: PDFFont): void {
    const text = this.winAnsiSafe(resolveCertificateVars(el.content, vars));
    const fontSize = el.fontSize * this.ptPerPx;
    const lineBox = fontSize * el.lineHeight;
    const maxWidth = el.w * this.ptPerPx;
    const xLeft = el.x * this.ptPerPx;

    // Parágrafos (\n) preservados; cada um passa pelo word-wrap.
    const lines = (text || '').split('\n')
      .map(p => this.wrapText(p, font, fontSize, maxWidth))
      .reduce((acc, cur) => acc.concat(cur.length ? cur : ['']), [] as string[]);

    lines.forEach((line, i) => {
      // Baseline espelhando o line-box do CSS: centro da linha + 0.35em.
      const baselineCanvasY = el.y + lineBox / this.ptPerPx * (i + 0.5) + 0.35 * el.fontSize;
      const y = this.pageH - baselineCanvasY * this.ptPerPx;
      const w = font.widthOfTextAtSize(line, fontSize);
      const x = el.align === 'center' ? xLeft + (maxWidth - w) / 2
        : el.align === 'right' ? xLeft + maxWidth - w
        : xLeft;
      if (line) {
        page.drawText(line, { x, y, size: fontSize, font, color: rgb(0, 0, 0) });
      }
    });
  }

  /** Word-wrap greedy usando a largura real da fonte (pdf-lib). */
  private wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
    const words = (text || '').split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let cur = '';
    for (const word of words) {
      const trial = cur ? cur + ' ' + word : word;
      if (cur && font.widthOfTextAtSize(trial, size) > maxWidth) { lines.push(cur); cur = word; } else { cur = trial; }
    }
    if (cur) { lines.push(cur); }
    return lines;
  }

  /** Troca chars tipográficos comuns por equivalentes ASCII (WinAnsi da fonte-padrão). */
  private winAnsiSafe(s: string): string {
    return (s || '')
      .replace(/[‘’]/g, '\'')
      .replace(/[“”]/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/…/g, '...');
  }
}
